import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useResetAdminPassword, type AdminUser } from "@/queries/adminUsers";

import {
  PasswordChoice,
  type ManualPasswordState,
  type PasswordMode,
  validateManual,
} from "./PasswordChoice";
import { PasswordRevealOnce } from "./PasswordRevealOnce";

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUser;
}

export function ResetPasswordDialog({
  open,
  onOpenChange,
  user,
}: ResetPasswordDialogProps) {
  const { t } = useTranslation("admin-users");
  const reset = useResetAdminPassword(user.id);

  const [mode, setMode] = useState<PasswordMode>("manual");
  const [manual, setManual] = useState<ManualPasswordState>({
    password: "",
    confirm: "",
  });
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  const resetLocal = () => {
    setMode("manual");
    setManual({ password: "", confirm: "" });
    setErrorKey(null);
    setGeneratedPassword(null);
    reset.reset();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetLocal();
    onOpenChange(next);
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorKey(null);

    if (mode === "manual") {
      const v = validateManual(manual);
      if (v) {
        setErrorKey(v);
        return;
      }
    }

    reset.mutate(
      mode === "manual"
        ? { password: manual.password }
        : { generate: true },
      {
        onSuccess: (data) => {
          if (data.generated_password) {
            setGeneratedPassword(data.generated_password);
          } else {
            handleOpenChange(false);
          }
        },
        onError: () => setErrorKey("generic"),
      },
    );
  };

  const submitDisabled =
    reset.isPending ||
    (mode === "manual" && (!manual.password || !manual.confirm));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("reset.title")}</DialogTitle>
          <DialogDescription>
            {t("reset.description", { email: user.email })}
          </DialogDescription>
        </DialogHeader>

        {generatedPassword ? (
          <div className="flex flex-col gap-4">
            <Alert>
              <AlertDescription>{t("reset.successHeading")}</AlertDescription>
            </Alert>
            <PasswordRevealOnce
              password={generatedPassword}
              onConfirmHide={() => {
                setGeneratedPassword(null);
                handleOpenChange(false);
              }}
            />
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            <PasswordChoice
              mode={mode}
              onModeChange={setMode}
              manual={manual}
              onManualChange={setManual}
              disabled={reset.isPending}
            />
            {errorKey ? (
              <Alert variant="destructive">
                <AlertDescription>{t(`reset.errors.${errorKey}`)}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={reset.isPending}
              >
                {t("reset.cancel")}
              </Button>
              <Button type="submit" disabled={submitDisabled}>
                {reset.isPending ? t("reset.submitting") : t("reset.submit")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
